import React from "react";
import { Box, Grid, InputLabel, MenuItem, useMediaQuery } from "@mui/material";

import styles from "../../sessions.module.css";
import { formErrorFor } from "./positionForm.errors";
import type { PositionFormik, SessionDates } from "./positionForm.types";
import { FieldError, MealOptionsField, OutlinedSelect } from "./PositionFormFields";
import ShiftTimeCells from "./ShiftTimeCells";
import { isSameDay, withDateStart, withOvernight } from "./positionForm.utils";

const NOT_SELECTED = "Select";
const OVERNIGHT_CHOICES = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const halfWidth = { width: "50%", "@media (max-width: 600px)": { width: "100%" } };

/**
 * Edit flow: one existing shift. The shift fields live directly on the form
 * values (`dateStart`, `timeIn`, `timeOut`, `tbd`, ...), not in `positionTimeInfo`.
 */
const EditShiftFields = ({ formik, session }: { formik: PositionFormik; session: SessionDates }) => {
  const { values, setValues, handleBlur, touched, errors } = formik;
  const isNarrow = useMediaQuery("(max-width:930px)");
  const isTbd = !!values?.tbd;

  // An overnight shift cannot start on the last day of the session.
  const startsOnLastDay = isSameDay(session?.sessionEnd, values?.dateStart);
  const overnightChoices = OVERNIGHT_CHOICES.filter(
    (choice) => choice.value === "no" || !startsOnLastDay,
  );
  const overnightValue =
    typeof values?.overNightShift === "boolean"
      ? values.overNightShift
        ? "yes"
        : "no"
      : NOT_SELECTED;

  return (
    <>
      <ShiftTimeCells
        shift={values}
        session={session}
        onShiftChange={(update) => setValues((prev) => update(prev))}
        onDateChange={(dateKey) => setValues((prev) => withDateStart(prev, dateKey))}
        onBlur={handleBlur}
        onHoursChange={formik.handleChange}
        errorFor={formErrorFor(formik)}
        dateLabel="Date Start"
        wideTimePicker
        widths={{
          date: { width: isTbd ? "50%" : "33.33%" },
          time: { width: "33.33%" },
          hours: { width: isNarrow ? "25%" : "50%" },
        }}
      />

      <Grid item sx={halfWidth}>
        <MealOptionsField {...formik} />
      </Grid>

      <Grid item sx={{ width: "50%" }}>
        <Box className={styles["form-box"]}>
          <InputLabel>Overnight</InputLabel>
          <OutlinedSelect
            name="overNightShift"
            value={overnightValue}
            onBlur={handleBlur}
            onChange={(e) => {
              const overnight = e?.target?.value === "yes";
              setValues((prev) => withOvernight(prev, overnight));
            }}
          >
            <MenuItem className={styles.option} value={NOT_SELECTED} disabled>
              {NOT_SELECTED}
            </MenuItem>
            {overnightChoices.map((choice) => (
              <MenuItem key={choice.value} value={choice.value} className={styles.option}>
                {choice.label}
              </MenuItem>
            ))}
          </OutlinedSelect>
          <FieldError show={touched?.overNightShift} message={errors?.overNightShift} />
        </Box>
      </Grid>
    </>
  );
};

export default EditShiftFields;
